import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { articlePath, formatArticleDate, getPublishedArticles } from "@/lib/articles";
import PageHero from "@/components/PageHero";

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: i * 0.06 },
  }),
};

export default function ArticlesPage() {
  const { t, lang } = useLanguage();
  const page = t.articles_page || {};
  const articles = getPublishedArticles(t);

  return (
    <main className="bg-white" data-testid="articles-page">
      <PageHero
        variant="light"
        size="balanced"
        eyebrow={page.eyebrow}
        title={page.title}
        description={page.subtitle}
        testId="articles-hero"
      />

      <section className="py-14 sm:py-20 lg:py-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-10 lg:px-20">
          {articles.length === 0 ? (
            <p className="mx-auto max-w-xl text-center text-base text-[#7a5c55]">
              {page.empty || (lang === "en" ? "Articles coming soon." : "Articolele urmează în curând.")}
            </p>
          ) : (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 lg:gap-7">
              {articles.map((article, i) => (
                <motion.article
                  key={article.slug}
                  variants={fadeUp}
                  custom={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, margin: "-40px" }}
                >
                  <Link
                    to={articlePath(article.slug)}
                    className="group flex h-full flex-col overflow-hidden rounded-[1.75rem] border border-[#3e2723]/8 bg-[#faf8f8] transition duration-500 hover:-translate-y-1 hover:bg-white hover:shadow-[0_20px_50px_-24px_rgba(62,39,35,0.2)]"
                    data-testid={`article-card-${article.slug}`}
                  >
                    <div className="relative aspect-[16/10] overflow-hidden bg-[#3e2723]/5">
                      {article.coverUrl ? (
                        <img
                          src={article.coverUrl}
                          alt=""
                          className="h-full w-full object-cover transition duration-700 group-hover:scale-[1.04]"
                          loading="lazy"
                        />
                      ) : (
                        <div className="absolute inset-0 bg-gradient-to-br from-[#fbd9e5] to-[#3e2723]/20" />
                      )}
                    </div>
                    <div className="flex flex-1 flex-col p-6 sm:p-7">
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#3e2723]/45">
                        {article.publishedAt ? <time dateTime={article.publishedAt}>{formatArticleDate(article.publishedAt, lang)}</time> : null}
                        {article.author ? <span>{article.author}</span> : null}
                      </div>
                      <h2 className="mt-3 font-display text-xl font-bold leading-snug tracking-tight text-[#3e2723] sm:text-[1.35rem]">
                        {article.title}
                      </h2>
                      {article.excerpt ? (
                        <p className="mt-2.5 line-clamp-3 text-sm leading-relaxed text-[#7a5c55]">{article.excerpt}</p>
                      ) : null}
                      <span className="mt-auto inline-flex items-center gap-1.5 pt-6 text-sm font-semibold text-[#3e2723]">
                        {page.read_more || (lang === "en" ? "Read article" : "Citește articolul")}
                        <ArrowUpRight className="h-4 w-4 transition group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                      </span>
                    </div>
                  </Link>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>
    </main>
  );
}
